import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AlertTriangle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import type { EvaluationData } from "@repo/results";
import { useClassementEntStruct } from "./useClassementEntStruct";

type Props = {
  /** Passations ent-struct de la campagne (`undefined` = chargement). */
  evaluations: EvaluationData[] | undefined;
  /** Lien qui ouvre la fiche d'un candidat (panneau), sur ce matching. */
  hrefCandidat: (parcoursId: string) => string;
};

/** Nombre de candidats affichés par défaut dans le classement. Au-delà, la
 *  lecture ligne à ligne n'apporte plus rien : le tableau sert à repérer le
 *  haut du classement, pas à parcourir toute la campagne. */
const TOP_CLASSEMENT = 5;

/**
 * Matching entretien structuré : classement des candidats sur le score global
 * pondéré. Une ligne ouvre la fiche du candidat, où se lit son radar.
 *
 * Le référentiel est ici le BARÈME DU TEMPLATE — les poids par compétence saisis
 * à la conception de l'entretien — et non un référentiel de référents comme en
 * PAMI ou fit-culturel : l'entretien structuré porte déjà sa cible.
 */
export function EntStructMatchingTab({ evaluations, hrefCandidat }: Props) {
  const naviguer = useNavigate();
  const [toutAfficher, setToutAfficher] = useState(false);

  const { classement, baremesHeterogenes } = useClassementEntStruct(evaluations);

  /** Le tableau n'affiche que le haut du classement. Le radar d'un candidat
   *  se lit dans sa fiche. */
  const classementAffiche = useMemo(
    () => (toutAfficher ? classement : classement.slice(0, TOP_CLASSEMENT)),
    [classement, toutAfficher],
  );

  if (!evaluations) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        Chargement des entretiens…
      </p>
    );
  }

  if (baremesHeterogenes) {
    return (
      <Card>
        <CardContent className="flex items-start gap-3 p-6">
          <AlertTriangle className="text-etat-alerte mt-0.5 h-5 w-5 shrink-0" />
          <div className="space-y-1 text-sm">
            <p className="font-medium">Comparaison impossible</p>
            <p className="text-muted-foreground">
              Les candidats de cette campagne n'ont pas passé le même entretien :
              les compétences ou leurs poids diffèrent d'un barème à l'autre. Un
              classement les mettrait sur une échelle commune qui n'existe pas.
              Les restitutions individuelles restent disponibles dans la fiche
              de chaque candidat.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Classement — INTRA-campagne uniquement : le score est une moyenne
          pondérée des taux de couverture du barème de CE template. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Classement des candidats{" "}
            {toutAfficher || classement.length <= TOP_CLASSEMENT
              ? `(${classement.length})`
              : `(${TOP_CLASSEMENT} meilleurs sur ${classement.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {classement.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              Aucun entretien FairView terminé dans cette campagne.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      #
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      Candidat
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      Score global pondéré
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {classementAffiche.map((entree) => {
                    const index = classement.indexOf(entree);
                    return (
                      <tr
                        key={entree.evaluation._id}
                        // La ligne entière ouvre la fiche ; le nom en est le
                        // vrai lien, pour le clavier et l'ouverture en onglet.
                        onClick={() =>
                          void naviguer(
                            hrefCandidat(entree.evaluation.parcoursId),
                          )
                        }
                        className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                      >
                        <td className="text-muted-foreground px-4 py-2 tabular-nums">
                          {entree.score === null ? "—" : index + 1}
                        </td>
                        <td className="px-4 py-2 font-medium">
                          <Link
                            to={hrefCandidat(entree.evaluation.parcoursId)}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline"
                          >
                            {entree.evaluation.prenomCandidat}{" "}
                            {entree.evaluation.nomCandidat}
                          </Link>
                        </td>
                        <td className="px-4 py-2">
                          {entree.score === null ? (
                            <span className="text-muted-foreground">
                              Entretien non exploitable
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="w-8 tabular-nums">
                                {Math.round(entree.score)}
                              </span>
                              <div className="bg-muted relative h-2 w-24 overflow-hidden rounded-full">
                                <div
                                  className="bg-primary h-full rounded-full"
                                  style={{
                                    width: `${Math.max(1, Math.min(100, entree.score))}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {classement.length > TOP_CLASSEMENT && (
                <div className="border-t p-3 text-center">
                  <button
                    type="button"
                    onClick={() => setToutAfficher((v) => !v)}
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    {toutAfficher
                      ? `Afficher seulement les ${TOP_CLASSEMENT} meilleurs`
                      : `Afficher les ${classement.length} candidats`}
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
